import { atom } from 'jotai'
import type { SetStateAction } from 'jotai'
import { Config } from './newConfigAtoms';

export const copyFromConfigAtom = atom<Config>({
    project_id: '',
    destination_id: '',
    lot_id: '',
    phase_id: '',
    unit_id: '',
    calculation_id: '',
    condition_id: '',
    graph_id: '',
});

// Generic "field" atom factory
function fieldAtom<K extends keyof Config>(key: K) {
    return atom(
        (get) => get(copyFromConfigAtom)[key],
        (get, set, update: Config[K]) =>
            set(copyFromConfigAtom, { ...get(copyFromConfigAtom), [key]: update }),
    )
};

export const projectIdAtom = atom(
    (get) => get(copyFromConfigAtom).project_id,
    (get, set, newProjectId: string) => {
        const prev = get(copyFromConfigAtom)
        set(copyFromConfigAtom, {
            ...prev,
            project_id: newProjectId,
            destination_id: '',
            lot_id: '',
            phase_id: '',
            unit_id: '',
            calculation_id: '',
        })
    }
);

export const destinationIdAtom = atom(
    (get) => get(copyFromConfigAtom).destination_id,
    (get, set, newDestinationId: string) => {
        const prev = get(copyFromConfigAtom)
        set(copyFromConfigAtom, {
            ...prev,
            destination_id: newDestinationId,
            lot_id: '',
            phase_id: '',
            unit_id: '',
            calculation_id: '',
        })
    }
);

export const lotIdAtom = atom(
    (get) => get(copyFromConfigAtom).lot_id,
    (get, set, newLotId: string) => {
        const prev = get(copyFromConfigAtom)
        set(copyFromConfigAtom, {
            ...prev,
            lot_id: newLotId,
            phase_id: '',
            unit_id: '',
            calculation_id: '',
        })
    }
);

export const phaseIdAtom = atom(
    (get) => get(copyFromConfigAtom).phase_id,
    (get, set, newPhaseId: string) => {
        const prev = get(copyFromConfigAtom)
        set(copyFromConfigAtom, {
            ...prev,
            phase_id: newPhaseId,
            unit_id: '',
            calculation_id: '',
        })
    }
);

export const unitIdAtom = atom(
    (get) => get(copyFromConfigAtom).unit_id,
    (get, set, newUnitId: string) => {
        const prev = get(copyFromConfigAtom)
        set(copyFromConfigAtom, {
            ...prev,
            unit_id: newUnitId,
            calculation_id: '',
        })
    }
);

export const calculationIdAtom = fieldAtom('calculation_id');

// Phenomenon & drive — primitive atoms
export const phenomenonIdAtom = atom<string | null>(null);
export const driveIdAtom = atom<string | null>(null);

// conditionIdAtom 
export const conditionIdAtom = atom<
    string | null,
    [SetStateAction<string | null>],
    void
>(
    /* READ ----------------------------------------------------------- */
    (get) => {
        const ph = get(phenomenonIdAtom)
        const dr = get(driveIdAtom)
        return ph && dr ? `${ph}-${dr}` : null
    },

    /* WRITE ---------------------------------------------------------- */
    (get, set, next) => {
        const value =
            typeof next === 'function' ? next(get(conditionIdAtom)) : next

        /* Push into the two source atoms ------------------------------ */
        if (!value) {
            set(phenomenonIdAtom, null)
            set(driveIdAtom, null)
        } else {
            const [ph, dr] = value.split('-')
            set(phenomenonIdAtom, ph || null)
            set(driveIdAtom, dr || null)
        }

        /* Also reflect in the root Config object ---------------------- */
        set(copyFromConfigAtom, {
            ...get(copyFromConfigAtom),
            condition_id: value ?? '',
        })
    },
);
